/**
 * StatusBadge — always dot + icon + text + colour; never colour alone. The soft colour pairs are the
 * design reference's tokens (--st-*). Labels come from i18n.
 */
import { useTranslation } from 'react-i18next';
import { cn } from '@/lib/utils';
import { Icon, type IconName } from './Icons';
import type { BadgeTone } from './ui/badge';

export type StatusKey =
  | 'proposed'
  | 'scheduled'
  | 'confirmed'
  | 'awaiting'
  | 'onHold'
  | 'inProgress'
  | 'completed'
  | 'cancelled'
  | 'noShow'
  | 'unable'
  | 'rescheduled'
  | 'surge'
  | 'staff'
  | 'facility';

export const STATUS_STYLE: Record<StatusKey, { tone: BadgeTone; icon: IconName; live?: boolean }> = {
  proposed: { tone: 'prop', icon: 'pencil' },
  scheduled: { tone: 'sched', icon: 'today' },
  confirmed: { tone: 'conf', icon: 'check' },
  awaiting: { tone: 'sched', icon: 'clock' },
  onHold: { tone: 'hold', icon: 'pause' },
  inProgress: { tone: 'conf', icon: 'clock', live: true },
  completed: { tone: 'done', icon: 'check' },
  cancelled: { tone: 'done', icon: 'x' },
  noShow: { tone: 'attn', icon: 'alert' },
  unable: { tone: 'attn', icon: 'alert' },
  rescheduled: { tone: 'done', icon: 'refresh' },
  surge: { tone: 'hold', icon: 'bolt' },
  staff: { tone: 'sched', icon: 'people' },
  facility: { tone: 'done', icon: 'building' },
};

/** SM_Appointments.AppointmentStatus (+ TenantConfirmationStatus) → the badge to show. */
export function appointmentStatusKey(appointmentStatus: string, confirmation?: string): StatusKey {
  switch (appointmentStatus) {
    case 'Proposed':
      return 'proposed';
    case 'On Hold':
      return 'onHold';
    case 'In Progress':
      return 'inProgress';
    case 'Completed':
      return 'completed';
    case 'Cancelled':
      return 'cancelled';
    case 'No Show':
      return 'noShow';
    case 'Unable to Complete':
      return 'unable';
    case 'Rescheduled':
      return 'rescheduled';
    case 'Scheduled':
    default:
      return confirmation === 'Confirmed' ? 'confirmed' : 'scheduled';
  }
}

export function StatusBadge({ status, className }: { status: StatusKey; className?: string }) {
  const { t } = useTranslation();
  const s = STATUS_STYLE[status];
  return (
    <span className={cn('status has-icon', s.tone, s.live && 'live', className)} data-status={status}>
      <span className="sdot" aria-hidden="true" />
      <Icon name={s.icon} />
      {t(`status.${status}`)}
    </span>
  );
}
